import { ReactNode, useEffect, useState } from "react";
import { Platform, View } from "react-native";
import { Text } from "react-native-paper";
import { Controller, useFormContext } from "react-hook-form";

import type { ScreenProps } from ".";
import { Anchor, Button, CheckboxItem, ActionsContainer, Modal, ScreenContainer, ScrollableContainer } from "../../components";
import { FormData } from "./formData";
import { PLAN_DETAILS_LINK } from "../links";
import useIntercom from "../../hooks/useIntercom";

interface AcceptProps {
  onContinue: () => void;
  visible: boolean;
  onDismiss: () => void;
}

function Bullet({ depth, children }: { depth?: 1; children: ReactNode }) {
  const margin = ["ml-2", "ml-8"][depth || 0];

  return (
    <View className="flex-row">
      <Text className={`text-regular-normal font-400 mr-2 ${margin}`}>•</Text>
      <Text className="text-regular-normal font-400 text-ink-dark flex-shrink">{children}</Text>
    </View>
  );
}

function Accept({ visible, onDismiss, onContinue }: AcceptProps) {
  const onContinuePress = () => {
    onContinue();
    onDismiss();
  };

  const { control, formState, trigger, getFieldState } = useFormContext<FormData>();
  const acceptedTerms = getFieldState("acceptedTerms", formState); // It is subscribed now and reactive to error state updated
  const valid = !acceptedTerms.error && acceptedTerms.isDirty;
  useEffect(() => {
    trigger("acceptedTerms");
  }, []);

  return (
    <Modal title="Terms Agreement" visible={visible} onDismiss={onDismiss}>
      <Controller
        name="acceptedTerms"
        control={control}
        rules={{ required: "Agreement is required" }}
        render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => (
          <CheckboxItem
            {...fieldProps}
            label='By checking the box and clicking "Agree & Continue" below, I am acknowledging and agreeing that:'
            labelStyle="text-regular-normal text-left mt-1 ml-1"
            className="items-start mx-0 my-4"
            error={error}
          />
        )}
      />
      <View className="ml-14 mr-6 mb-4">
        <Bullet>I have had the opportunity to read and have read both the "Terms & Conditions" and the Plan;</Bullet>
        <Bullet>I accept and will comply with "Terms & Conditions" and the Plan; and</Bullet>
        <Bullet>My participation in the Plan will be governed by the "Terms and Conditions" and the Plan.</Bullet>

        <Text className="mt-4 text-regular-normal font-400 text-ink-dark">
          Plan details can be found{" "}
          <Anchor aClassName="text-primary underline" target="_blank" href={PLAN_DETAILS_LINK}>
            here
          </Anchor>
          .
        </Text>
      </View>

      <ActionsContainer horizontalStyle="web" insideModal>
        <Button mode="contained" disabled={!valid} onPress={onContinuePress}>
          Agree & Continue
        </Button>

        <Button mode="text" onPress={onDismiss}>
          Cancel
        </Button>
      </ActionsContainer>
    </Modal>
  );
}

export default function TermsScreen({ navigation: { navigate } }: ScreenProps<"Terms">) {
  const [showModal, setShowModal] = useState(false);

  useIntercom({ onFocus: { event: "viewed-enrollment-terms" } });

  return (
    <ScreenContainer navigationTitle="Terms & Conditions" goBack={() => navigate("Overview")}>
      <ScrollableContainer containerClassName="px-7" contentContainerClassName="py-9" testID="terms-screen">
        <Text className="text-ink-darkest text-title-4 font-700">Terms & Conditions</Text>
        <Text className="mt-5 text-regular-normal font-400 text-ink-dark">
          I understand and agree that the PGA of America’s Deferred Compensation Plan (“Plan”) is a nonelective, nonqualified
          deferred compensation plan sponsored by The PGA of America and is subject to requirements under the United States
          Internal Revenue Code (“Code”) and the plan document, including the following:
        </Text>
        <Bullet>Contributions to the Plan:</Bullet>
        <Bullet depth={1}>
          Contributions to the Plan are made only by The Professional Golfers' Association of America (“The PGA of America”).
          There are no participant contributions.
        </Bullet>
        <Bullet depth={1}>
          The PGA of America will determine the amount of funds, if any, that it commits to the Plan on an annual basis. The
          maximum initial plan commitment will be $15 million. Moving forward the maximum annual commitment will be the higher
          of $15 million or 5% of PGA’s annual revenue for the past four years. There is no minimum contribution and PGA may
          reduce the commitment (including to zero) at any time.
        </Bullet>
        <Bullet>
          Eligible members earn points under the Plan by performing certain qualifying activities set forth in the Points
          Matrix. To be eligible for Plan benefits, an eligible member must attain a minimum of 200 points during any given plan
          year. Employees of PGA of America are not eligible to participate in the Plan. Notwithstanding anything to the
          contrary in the Plan, currently points will only be awarded for activities in the United States of America (limited to
          the fifty (50) states and excluding all territories and possessions of the United States of America) as outlined in
          the Points Matrix.
        </Bullet>
        <Bullet>
          PGA of America Members in Class F are not eligible to earn points. PGA of America membership status shall be
          determined by the PGA of America in its sole discretion based on the membership classification rules established in
          the PGA of America’s Constitution and Bylaws.
        </Bullet>
        <Bullet>
          Plan participants must track these qualifying activities and complete an attestation for each of their completed,
          qualifying activities on an ongoing basis and/or annually within 60 days of the end of the plan year. This attestation
          must be counter-executed by another PGA member. The PGA of America reserves the right to require additional evidence
          to demonstrate completion of qualifying activities.
        </Bullet>
        <Bullet>
          Plan assets are owned by The PGA of America until distributed and are subject to claims of the PGA of America’s
          general creditors in the event of The PGA of America’s insolvency.
        </Bullet>
        <Bullet>
          Distributions are restricted by the Plan and the Code. In general, a Plan Participant will receive Plan benefits in
          monthly installment payments over a 10 year period beginning 90 days after the first of April following the date when
          such Plan Participant turns 65 years of age. The Plan allows distributions prior to April 1 of the year following
          attainment of age 65 <Text className="text-regular-normal font-400 text-ink-dark underline">only</Text> in the event
          of death, disability, incompetency or an unforeseeable emergency (as such terms are defined by the Plan)
        </Bullet>
        <Bullet>
          No money will be withheld from plan distributions to pay income taxes. Members are responsible for paying any taxes
          owed. You should consult your tax professional for any needed assistance.
        </Bullet>
        <Bullet>
          Plan Participants may earn points after the first of April following the date that such Participant turns 65 years of
          age, but any amount allocated to such Participant’s account from points earned after the first of April following the
          date that such Participant turns 65 years of age shall be distributed in a single lump sum as outlined in the Plan.
        </Bullet>
        <Bullet>
          Distributions from this plan will <Text className="text-regular-normal font-700 text-ink-dark underline">not</Text> be
          eligible to be rolled over to other employer retirement plans or IRAs. No rollovers are allowed into this plan.
        </Bullet>
        <Bullet>
          Loans are <Text className="text-regular-normal font-700 text-ink-dark underline">not</Text> available under the Plan.
        </Bullet>
        <Bullet>
          PGA of America may engage a third-party administrator to assist with the management and operation of the Plan. Each
          Plan Participant consents to PGA of America’s sharing of such Plan Participant’s Plan related information with such
          third-party administrator, as determined necessary by PGA of America.
        </Bullet>
        <Bullet>
          PGA of America may take any action or decide any matter under the terms of the Plan in its sole and absolute
          discretion. No Plan Participant has any ownership interest in, or right to, any funds held by PGA for the Plan or
          otherwise. All disputes arising out of or related to the Plan shall be resolved on an individual basis, and not as a
          class action, representative action, class arbitration, or similar proceeding.
        </Bullet>
        <Bullet>
          Plan Participants agree to act with integrity and professionalism in connection with your participation in the Plan,
          and that your failure to do so may be subject to review under the PGA’s Code of Ethics.
        </Bullet>
        <Bullet>
          PGA of America may amend or terminate the Plan at any time. In the event of any conflict between this document and the
          Plan rules as described in the Plan document, the Plan rules shall govern.
        </Bullet>
        <Bullet>
          Link to the plan can be found{" "}
          <Anchor aClassName="text-primary underline" target="_blank" href={PLAN_DETAILS_LINK}>
            here
          </Anchor>
        </Bullet>
      </ScrollableContainer>

      <ActionsContainer>
        <Button mode="contained" onPress={() => setShowModal(true)} testID="terms-continue">
          Accept Terms
        </Button>
      </ActionsContainer>

      <Accept
        visible={showModal}
        onContinue={() => {
          if (Platform.OS === "web") {
            // @ts-ignore
            window.scrollTo({ top: 0, behavior: "instant" });
          }
          navigate("AccountDetails");
        }}
        onDismiss={() => setShowModal(false)}
      />
    </ScreenContainer>
  );
}
